import { create } from 'zustand';

// Fuente única de verdad para mic/cámara/screen share, leída/escrita directo por
// Map.jsx/MenuMedia.jsx sin props ni CustomEvent. Reemplaza mediaActions.mic/cam/share
// (Home.jsx) + el CustomEvent 'media-pending-change' anterior. rec/rec_audio se fueron a
// useScreenRecordStore; selectedAudioDeviceId -> audioDeviceId acá (§1a) -- es el input de
// mic elegido en el picker de MenuMedia y consumido por el getUserMedia del mic en Map.
//
// Integración main→dev, epic C3 (`5db0dda5` / `9a3d9b86`): sincronización entre pestañas del
// estado de los botones multimedia. Solo la pestaña master (elegida por `useMasterTabElection`,
// canal `colonny_map_webrtc`) sostiene el hardware real; una pestaña follower que aprieta
// mic/cam publica su intención por `colonny_media_sync` para que la master la lea y active su
// efecto de hardware, y la master publica de vuelta el estado físico (pending/normal/error)
// para que todas las pestañas muestren el mismo spinner/ícono. `isMasterTab` acá es un espejo
// que `useMapMediasoup` sincroniza desde el hook de elección — la fuente de verdad sigue
// siendo ese hook.
const initialSlice = { requested: false, status: 'normal' }; // status: 'normal'|'pending'|'error'
const errorTimers = {};

// `BroadcastChannel` puede no existir (SSR / navegadores viejos) — degradar a un stub no-op.
const mediaSyncChannel = typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new window.BroadcastChannel('colonny_media_sync')
    : { postMessage() {}, close() {}, onmessage: null };

export const useMediaStore = create((set, get) => {
    mediaSyncChannel.onmessage = (event) => {
        const { type, key, value, status } = event.data || {};
        if (type === 'SYNC_REQUEST') {
            set((state) => ({ [key]: { ...state[key], requested: value } }));
        } else if (type === 'SYNC_STATUS') {
            set((state) => ({ [key]: { ...state[key], status } }));
        } else if (type === 'SYNC_FOCUS_MASTER') {
            if (get().isMasterTab && typeof window !== 'undefined') {
                window.focus();
                const originalTitle = document.title;
                document.title = '👑 aquí estoy 👑';
                setTimeout(() => { document.title = originalTitle; }, 1500);
            }
        }
    };

    return {
        mic: { ...initialSlice },
        cam: { ...initialSlice },
        share: { ...initialSlice },

        // Espejo de `useMasterTabElection().isMasterTab` (lo escribe `useMapMediasoup`).
        isMasterTab: false,
        setIsMasterTab: (value) => set({ isMasterTab: !!value }),

        // Un follower pide a la master que traiga su ventana al frente (icono de "ir a la
        // pestaña master" — integración main→dev `9a3d9b86`).
        focusMasterTab: () => mediaSyncChannel.postMessage({ type: 'SYNC_FOCUS_MASTER' }),

        // deviceId del input de audio para grabar/transmitir, o null = default del sistema.
        // Preferencia del usuario: NO se limpia en reset() (que es privacidad al salir del mapa).
        audioDeviceId: null,
        setAudioDeviceId: (id) => set({ audioDeviceId: id || null }),

        toggleRequested: (key) => set((state) => {
            if (state[key].status === 'pending') return state;
            const requested = !state[key].requested;
            mediaSyncChannel.postMessage({ type: 'SYNC_REQUEST', key, value: requested });
            return { [key]: { ...state[key], requested } };
        }),

        setRequested: (key, value) => set((state) => {
            mediaSyncChannel.postMessage({ type: 'SYNC_REQUEST', key, value });
            return { [key]: { ...state[key], requested: value } };
        }),

        // Reemplaza el CustomEvent 'media-pending-change'. Guard "no pisar error con normal": el
        // `finally` de los 3 efectos de media en Map.jsx siempre dispara setStatus(key,'normal')
        // justo después de un posible setStatus(key,'error') del catch -- sin este guard el estado
        // de error nunca se vería.
        setStatus: (key, status) => set((state) => {
            if (status !== 'error' && state[key].status === 'error') return state;
            clearTimeout(errorTimers[key]);
            if (status === 'error') {
                errorTimers[key] = setTimeout(() => {
                    useMediaStore.setState((s) => {
                        if (s[key].status !== 'error') return s;
                        mediaSyncChannel.postMessage({ type: 'SYNC_STATUS', key, status: 'normal' });
                        return { [key]: { ...s[key], status: 'normal' } };
                    });
                }, 3000);
            }
            mediaSyncChannel.postMessage({ type: 'SYNC_STATUS', key, status });
            return { [key]: { ...state[key], status } };
        }),

        reset: () => set({ mic: { ...initialSlice }, cam: { ...initialSlice }, share: { ...initialSlice } }),
    };
});
